// Footer2.js - Contains functions for creating a minimalist Footer section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a minimalist Footer section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the footer is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer elements
 */
export const createFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const footerSectionClass = 'footer2-section';
  const footerContainerClass = 'footer2-container';
  const footerContentClass = 'footer2-content';
  const footerLogoClass = 'footer2-logo';
  const footerNavClass = 'footer2-nav';
  const footerLinkClass = 'footer2-link';
  const footerSocialClass = 'footer2-social';
  const footerSocialLinkClass = 'footer2-social-link';
  const footerDividerClass = 'footer2-divider';
  const footerCopyrightClass = 'footer2-copyright';
  const footerLegalClass = 'footer2-legal';
  const footerLegalLinkClass = 'footer2-legal-link';

  // Desktop Styles (default)
  const footerSectionStyles = {
    position: 'relative',
    paddingTop: '60px',
    paddingBottom: '40px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#ffffff',
    color: '#111111',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTop: '1px solid #eaeaea'
  };

  const footerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '1000px',
    width: '100%',
    margin: '0 auto',
    gap: '40px',
    textAlign: 'center'
  };

  const footerContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px',
    width: '100%'
  };

  const footerLogoStyles = {
    fontSize: '28px',
    fontWeight: '800',
    color: '#111111',
    textDecoration: 'none',
    letterSpacing: '-0.5px'
  };

  const footerNavStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '24px',
    margin: '0 auto'
  };

  const footerLinkStyles = {
    fontSize: '15px',
    fontWeight: '500',
    color: '#333333',
    textDecoration: 'none',
    transition: 'color 0.2s ease'
  };

  const footerSocialStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '20px',
    marginTop: '16px'
  };

  const footerSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    color: '#111111',
    backgroundColor: '#f5f5f5',
    transition: 'all 0.2s ease'
  };

  const footerDividerStyles = {
    width: '80px',
    height: '2px',
    backgroundColor: '#111111',
    margin: '16px auto',
    opacity: '0.1'
  };

  const footerCopyrightStyles = {
    fontSize: '14px',
    color: '#666666',
    marginTop: '8px'
  };

  const footerLegalStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '16px'
  };

  const footerLegalLinkStyles = {
    fontSize: '13px',
    color: '#666666',
    textDecoration: 'none'
  };

  // Tablet Styles
  const tabletFooterNavStyles = {
    ...footerNavStyles,
    gap: '20px'
  };

  // Mobile Styles
  const mobileFooterNavStyles = {
    ...footerNavStyles,
    flexDirection: 'column',
    gap: '16px'
  };

  const mobileFooterSocialStyles = {
    ...footerSocialStyles,
    marginTop: '24px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerSectionClass, footerSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContainerClass, footerContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContentClass, footerContentStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoClass, footerLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerNavClass, footerNavStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkClass, footerLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialClass, footerSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialLinkClass, footerSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDividerClass, footerDividerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerCopyrightClass, footerCopyrightStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLegalClass, footerLegalStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLegalLinkClass, footerLegalLinkStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(footerNavClass, tabletFooterNavStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerNavClass, mobileFooterNavStyles);
    newRegistry[DEVICES.MOBILE].set(footerSocialClass, mobileFooterSocialStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerSectionClass,
    footerContainerClass,
    footerContentClass,
    footerLogoClass,
    footerNavClass,
    footerLinkClass,
    footerSocialClass,
    footerSocialLinkClass,
    footerDividerClass,
    footerCopyrightClass,
    footerLegalClass,
    footerLegalLinkClass
  ]));

  // Generate IDs for all elements
  const footerSectionId = Date.now();
  const containerID = Date.now() + 1;
  const contentID = Date.now() + 2;
  const logoID = Date.now() + 3;
  const navID = Date.now() + 4;
  
  // Nav links
  const link1ID = Date.now() + 5;
  const link2ID = Date.now() + 6;
  const link3ID = Date.now() + 7;
  const link4ID = Date.now() + 8;
  const link5ID = Date.now() + 9;
  
  // Social links
  const socialWrapperID = Date.now() + 10;
  const social1ID = Date.now() + 11;
  const social2ID = Date.now() + 12;
  const social3ID = Date.now() + 13;
  const social4ID = Date.now() + 14;
  
  // Generate IDs for social media icons
  const social1IconId = Date.now() + 15;
  const social2IconId = Date.now() + 16;
  const social3IconId = Date.now() + 17;
  const social4IconId = Date.now() + 18;
  
  // Divider
  const dividerID = Date.now() + 19;
  
  // Copyright and legal
  const copyrightID = Date.now() + 20;
  const legalWrapperID = Date.now() + 21;
  const legal1ID = Date.now() + 22;
  const legal2ID = Date.now() + 23;
  const legal3ID = Date.now() + 24;

  const newElements = [
    // Main Footer Section Container
    {
      id: footerSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Footer Section',
      parentId: null,
      className: footerSectionClass,
      styles: {},
      children: [containerID],
      text: '',
      attributes: {
        'data-element': 'footer-section',
        'data-component': 'section',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Container',
      parentId: footerSectionId,
      hierarchyName: "Footer Container",
      className: footerContainerClass,
      styles: {},
      children: [contentID],
      text: '',
      attributes: {
        'data-element': 'footer-container'
      }
    },
    
    // Content
    {
      id: contentID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Content',
      parentId: containerID,
      hierarchyName: "Footer Content",
      className: footerContentClass,
      styles: {},
      children: [logoID, navID, socialWrapperID, dividerID, copyrightID, legalWrapperID],
      text: '',
      attributes: {
        'data-element': 'footer-content'
      }
    },
    
    // Logo
    {
      id: logoID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Logo',
      parentId: contentID,
      hierarchyName: "Footer Logo",
      className: footerLogoClass,
      styles: {},
      children: [],
      text: 'SUMMIT',
      attributes: {
        'data-element': 'footer-logo',
        'href': '#'
      },
      interactions: {
        events: [{
          id: Date.now(),
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '1s',
            delay: '0.1s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Navigation Links
    {
      id: navID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'nav',
      name: 'Footer Navigation',
      parentId: contentID,
      hierarchyName: "Footer Navigation",
      className: footerNavClass,
      styles: {},
      children: [link1ID, link2ID, link3ID, link4ID, link5ID],
      text: '',
      attributes: {
        'data-element': 'footer-nav',
        'aria-label': 'Footer Navigation'
      }
    },
    
    // Nav Link 1
    {
      id: link1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 1',
      parentId: navID,
      hierarchyName: "Nav Link 1",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Home',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 2
    {
      id: link2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 2',
      parentId: navID,
      hierarchyName: "Nav Link 2",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Features',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 3
    {
      id: link3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 3',
      parentId: navID,
      hierarchyName: "Nav Link 3",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Pricing',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 4
    {
      id: link4ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 4',
      parentId: navID,
      hierarchyName: "Nav Link 4",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'About',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Nav Link 5
    {
      id: link5ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Nav Link 5',
      parentId: navID,
      hierarchyName: "Nav Link 5",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Contact',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Social Links
    {
      id: socialWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: contentID,
      hierarchyName: "Social Links",
      className: footerSocialClass,
      styles: {},
      children: [social1ID, social2ID, social3ID, social4ID],
      text: '',
      attributes: {
        'data-element': 'footer-social'
      }
    },
    
    // Social Link 1 - Twitter
    {
      id: social1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 1',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 1",
      className: footerSocialLinkClass,
      styles: {},
      children: [social1IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Twitter'
      }
    },

    // Twitter Icon
    {
      id: social1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: social1ID,
      hierarchyName: "Twitter Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%23111111',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Twitter'
      }
    },
    
    // Social Link 2 - Facebook
    {
      id: social2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 2',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 2",
      className: footerSocialLinkClass,
      styles: {},
      children: [social2IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Facebook'
      }
    },

    // Facebook Icon
    {
      id: social2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: social2ID,
      hierarchyName: "Facebook Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%23111111',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },
    
    // Social Link 3 - Instagram
    {
      id: social3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 3',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 3",
      className: footerSocialLinkClass,
      styles: {},
      children: [social3IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Instagram'
      }
    },

    // Instagram Icon
    {
      id: social3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: social3ID,
      hierarchyName: "Instagram Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%23111111',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },
    
    // Social Link 4 - LinkedIn
    {
      id: social4ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 4',
      parentId: socialWrapperID,
      hierarchyName: "Social Link 4",
      className: footerSocialLinkClass,
      styles: {},
      children: [social4IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'LinkedIn'
      }
    },

    // LinkedIn Icon
    {
      id: social4IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: social4ID,
      hierarchyName: "LinkedIn Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%23111111',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'LinkedIn'
      }
    },
    
    // Divider
    {
      id: dividerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Divider',
      parentId: contentID,
      hierarchyName: "Footer Divider",
      className: footerDividerClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'footer-divider'
      }
    },
    
    // Copyright
    {
      id: copyrightID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Copyright',
      parentId: contentID,
      hierarchyName: "Footer Copyright",
      className: footerCopyrightClass,
      styles: {},
      children: [],
      text: `© ${new Date().getFullYear()} Summit, Inc. All rights reserved.`,
      attributes: {
        'data-element': 'footer-copyright'
      }
    },
    
    // Legal Links
    {
      id: legalWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Legal Links',
      parentId: contentID,
      hierarchyName: "Legal Links",
      className: footerLegalClass,
      styles: {},
      children: [legal1ID, legal2ID, legal3ID],
      text: '',
      attributes: {
        'data-element': 'footer-legal'
      }
    },
    
    // Legal Link 1
    {
      id: legal1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Legal Link 1',
      parentId: legalWrapperID,
      hierarchyName: "Legal Link 1",
      className: footerLegalLinkClass,
      styles: {},
      children: [],
      text: 'Privacy Policy',
      attributes: {
        'data-element': 'legal-link',
        'href': '#'
      }
    },
    
    // Legal Link 2
    {
      id: legal2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Legal Link 2',
      parentId: legalWrapperID,
      hierarchyName: "Legal Link 2",
      className: footerLegalLinkClass,
      styles: {},
      children: [],
      text: 'Terms of Service',
      attributes: {
        'data-element': 'legal-link',
        'href': '#'
      }
    },
    
    // Legal Link 3
    {
      id: legal3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Legal Link 3',
      parentId: legalWrapperID,
      hierarchyName: "Legal Link 3",
      className: footerLegalLinkClass,
      styles: {},
      children: [],
      text: 'Cookie Policy',
      attributes: {
        'data-element': 'legal-link',
        'href': '#'
      }
    }
  ];
  
  // Return all elements
  return {
    footerSection: newElements[0],
    elements: newElements
  };
};

export default createFooterElements; 